import type { ApiErrorResponse, PublicRuntimeConfig, ScanResponse } from "../shared/contracts";
import { parseScanResponse } from "../shared/validation";

export class ScanApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string
  ) {
    super(code);
  }
}

async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new ScanApiError(response.status, "invalid_response");
  }
}

function runtimeConfig(value: unknown): PublicRuntimeConfig | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const data = value as Record<string, unknown>;
  if (typeof data.turnstileEnabled !== "boolean") return null;
  const config: PublicRuntimeConfig = {
    turnstileEnabled: data.turnstileEnabled
  };
  if (data.turnstileEnabled) {
    if (typeof data.turnstileSiteKey !== "string" || !data.turnstileSiteKey.trim()) return null;
    config.turnstileSiteKey = data.turnstileSiteKey;
  }
  return config;
}

export async function fetchRuntimeConfig(fetcher: typeof fetch = fetch): Promise<PublicRuntimeConfig> {
  try {
    const response = await fetcher("/api/config", { headers: { Accept: "application/json" } });
    if (!response.ok) return { turnstileEnabled: false };
    return runtimeConfig(await readJson<unknown>(response)) ?? { turnstileEnabled: false };
  } catch {
    return { turnstileEnabled: false };
  }
}

export async function requestScan(
  turnstileToken?: string,
  fetcher: typeof fetch = fetch
): Promise<ScanResponse> {
  const response = await fetcher("/api/scan", {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(turnstileToken ? { turnstileToken } : {})
  });
  if (!response.ok) {
    const payload = await readJson<ApiErrorResponse>(response);
    throw new ScanApiError(response.status, payload.error?.code ?? "request_failed");
  }
  const payload = await readJson<unknown>(response);
  const parsed = parseScanResponse(payload);
  if (!parsed) throw new ScanApiError(response.status, "invalid_response");
  return parsed;
}
